/**
 * Debug utility to track component re-renders and identify infinite loops
 * Add this to components suspected of causing React error #62
 */

const renderCounts = new Map();
const renderTimes = new Map();
const LOOP_THRESHOLD = 10; // LOWERED: Alert if component renders more than 10 times in 2 seconds
const WARNING_THRESHOLD = 5; // Warn at 5 renders
const TIME_WINDOW = 2000; // Check 2 second window instead of 5

export function trackRender(componentName, props = {}) {
  const now = Date.now();

  // Initialize tracking for this component
  if (!renderCounts.has(componentName)) {
    renderCounts.set(componentName, 0);
    renderTimes.set(componentName, []);
  }

  // Increment render count
  const count = renderCounts.get(componentName) + 1;
  renderCounts.set(componentName, count);

  // Track render time
  const times = renderTimes.get(componentName);
  times.push(now);

  // Keep only renders from last 2 seconds (more sensitive)
  const recentTimes = times.filter(t => now - t < TIME_WINDOW);
  renderTimes.set(componentName, recentTimes);

  // ALWAYS log with full prop details for debugging
  const propDetails = JSON.stringify(props, null, 2);

  // Check for potential infinite loop (more aggressive)
  if (recentTimes.length > LOOP_THRESHOLD) {
    console.error(
      `🚨 INFINITE LOOP DETECTED: ${componentName} rendered ${recentTimes.length} times in 2 seconds`,
      '\nTotal renders:', count,
      '\nProps:', propDetails
    );

    // Log to help debug
    console.trace('Render stack trace');

    // Try to break the loop by throwing
    throw new Error(`INFINITE LOOP: ${componentName} - Check console for details`);
  } else if (recentTimes.length > WARNING_THRESHOLD) {
    console.warn(
      `⚠️ SUSPICIOUS: ${componentName} rendered ${recentTimes.length} times in 2 seconds`,
      '\nTotal renders:', count,
      '\nProps:', propDetails
    );
  } else {
    console.log(
      `🔄 ${componentName} render #${count} (${recentTimes.length} in last 2s)`,
      '\nProps:', propDetails
    );
  }
}

export function resetTracking(componentName) {
  if (componentName) {
    renderCounts.delete(componentName);
    renderTimes.delete(componentName);
  } else {
    renderCounts.clear();
    renderTimes.clear();
  }
}

export function getStats() {
  const stats = {};
  renderCounts.forEach((count, name) => {
    const times = renderTimes.get(name);
    stats[name] = {
      totalRenders: count,
      recentRenders: times.length
    };
  });
  return stats;
}
